import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, Search, Mail, Calendar, ShoppingBag, Shield, User, Trash2, Code, ChevronDown
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const PCS_MASTER_EMAIL = 'pcsdutoseexaustores@gmail.com';

const roleConfig = {
  customer: { label: 'Cliente', color: 'bg-blue-500', icon: User },
  admin: { label: 'Admin', color: 'bg-purple-500', icon: Shield },
  programmer: { label: 'Programador', color: 'bg-emerald-500', icon: Code },
};

export const AdminUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const token = localStorage.getItem('adminToken');
  const currentUserRole = localStorage.getItem('userRole');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  
  const isMasterProgrammer = currentUserEmail === PCS_MASTER_EMAIL;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    if (q) setSearchTerm(q);
  }, [location.search]);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUsers();
    fetchCurrentUser();
    const interval = setInterval(() => {
      fetchUsers();
    }, 30000);
    return () => clearInterval(interval);
  }, [navigate, token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCurrentUserEmail(response.data.email);
    } catch (error) {
      console.error('Error fetching current user:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, 
        { role: newRole },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      fetchUsers();
      alert(`Cargo alterado para ${roleConfig[newRole].label} com sucesso!`);
    } catch (error) {
      console.error('Error changing role:', error);
      alert(error.response?.data?.detail || 'Erro ao alterar cargo');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const { darkMode, theme } = useAdminTheme();

  return (
    <AdminLayout title="Clientes" subtitle="Gerencie os clientes cadastrados">
      {/* Stats */}
      <div data-tour="users-stats" className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Total de Usuários</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Code className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Programadores</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{users.filter(u => u.role === 'programmer').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Administradores</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{users.filter(u => u.role === 'admin').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/30 rounded-xl flex items-center justify-center">
              <User className="h-6 w-6 text-sky-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Clientes</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{users.filter(u => u.role === 'customer').length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${theme.textMuted}`} />
          <Input
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 ${theme.input} rounded-xl`}
          />
        </div>
      </div>

      {/* Users Table */}
      <Card data-tour="users-table" className={`${theme.card} border rounded-2xl`}>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={theme.tableRow}>
                  <TableHead className={theme.tableHead}>Cliente</TableHead>
                  <TableHead className={theme.tableHead}>Email</TableHead>
                  <TableHead className={theme.tableHead}>Cadastro</TableHead>
                  <TableHead className={theme.tableHead}>Pedidos</TableHead>
                  <TableHead className={theme.tableHead}>Tipo</TableHead>
                  <TableHead className={theme.tableHead}>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => (
                  <TableRow key={user.id} className={theme.tableRow}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className={`${theme.text} font-medium`}>{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center space-x-2 ${theme.textSecondary}`}>
                        <Mail className="h-4 w-4" />
                        <span>{user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center space-x-2 ${theme.textSecondary}`}>
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(user.created_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center space-x-2 ${theme.textSecondary}`}>
                        <ShoppingBag className="h-4 w-4" />
                        <span>{user.order_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {isMasterProgrammer && user.email !== PCS_MASTER_EMAIL && user.email !== currentUserEmail ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className={`${roleConfig[user.role]?.color} text-white hover:opacity-80`}
                            >
                              {roleConfig[user.role]?.label || user.role}
                              <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className={theme.dropdown}>
                            <DropdownMenuItem 
                              onClick={() => handleChangeRole(user.id, 'customer')}
                              className={`${theme.text} cursor-pointer`}
                            >
                              <User className="h-4 w-4 mr-2 text-blue-500" />
                              Cliente
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleChangeRole(user.id, 'admin')}
                              className={`${theme.text} cursor-pointer`}
                            >
                              <Shield className="h-4 w-4 mr-2 text-purple-500" />
                              Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleChangeRole(user.id, 'programmer')}
                              className={`${theme.text} cursor-pointer`}
                            >
                              <Code className="h-4 w-4 mr-2 text-emerald-500" />
                              Programador
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Badge className={roleConfig[user.role]?.color || 'bg-blue-500'}>
                          {roleConfig[user.role]?.label || user.role}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {isMasterProgrammer && user.email !== PCS_MASTER_EMAIL && user.email !== currentUserEmail && (
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDelete(user.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};
